import { getLocationBySlug, getAllQualifiedLocations, getAllSupportedStates, getAllSupportedCountries, isLocationQualified } from "./locationRegistry";
import { isSeoIndexEligible } from "./seoEligibility";
import { INDUSTRY_REGISTRY, getAllIndustries } from "./industryRegistry";
import { GUIDE_REGISTRY, getAllGuides } from "./guideRegistry";
import { FEATURE_REGISTRY, getAllFeatures } from "./featureRegistry";
import { REPORT_REGISTRY, getAllReports } from "./reportRegistry";
import { METHODOLOGY_REGISTRY, getAllMethodologyArticles } from "./methodologyRegistry";
import { COMPARE_REGISTRY, getAllCompareArticles } from "./compareRegistry";
import { NICHE_JOB_INTENTS } from "./nicheIntentRegistry";
import { generateTier1StateMetadata, generateTier2CityMetadata, generateTier3NicheMetadata } from "./seoProgrammaticEngine";

import { getAllJobCategoryDefinitions, getJobCategoryBySlug, getCategoryBaseName, sanitizeDuplicateWords } from "./jobCategoryRegistry";
import { getSeoRedirect } from "./seoRedirects";

export type IndexingIntent =
  | "index"
  | "noindex"
  | "redirect"
  | "gone"
  | "canonical_duplicate";

export type PageType = 
  | "core" 
  | "location" 
  | "location-directory" 
  | "industry" 
  | "industry-directory" 
  | "industry-location" 
  | "guide" 
  | "guide-hub" 
  | "feature" 
  | "feature-hub" 
  | "methodology" 
  | "report" 
  | "report-hub" 
  | "compare" 
  | "compare-hub"
  | "explore-hub"
  | "state-hub"
  | "legal";

export interface SeoRegistryEntry {
  route: string;
  pageType: PageType;
  title: string;
  metaDescription: string;
  canonicalUrl: string;
  h1: string;
  breadcrumbLabel: string;
  sitemapInclusion: boolean;
  indexingStatus: "index, follow" | "noindex, follow";
  indexingIntent?: IndexingIntent;
  lastmod: string;
  structuredDataType: string;
  relatedRoutes: string[];
  publicationStatus: "published" | "disabled";
  overview?: string;
  directAnswer?: string;
  keyTakeaways?: string[];
  contentSections?: any[];
  sampleScript?: any;
  safetyWarning?: string;
}

const PRIMARY_DOMAIN = "https://getbiznearyou.com";

// Core Static Public Pages
const CORE_PAGES: SeoRegistryEntry[] = [
  {
    route: "/jobs",
    pageType: "core",
    title: "Local Business Job Directory & Employment Hub | BizNearYou",
    metaDescription: "Central local job directory connecting you with nearby business hiring signals, entry-level positions, student opportunities, and job-type silos.",
    canonicalUrl: `${PRIMARY_DOMAIN}/jobs`,
    h1: "Local Business Job Directory & Employment Hub",
    breadcrumbLabel: "Jobs Directory",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-30",
    structuredDataType: "WebSite",
    relatedRoutes: ["/find-jobs", "/hidden-jobs", "/businesses-hiring-near-me", "/first-jobs", "/local-internships"],
    publicationStatus: "published"
  },
  {
    route: "/",
    pageType: "core",
    title: "Find Local Businesses That May Be Hiring Near You | BizNearYou",
    metaDescription: "Search nearby businesses, review recent hiring signals, and find employers you may want to contact directly—even when they have not posted a job on a major job board.",
    canonicalUrl: `${PRIMARY_DOMAIN}/`,
    h1: "Find Local Businesses That May Be Hiring Near You",
    breadcrumbLabel: "Home",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "WebSite",
    relatedRoutes: ["/for", "/find-jobs", "/hidden-jobs", "/businesses-hiring-near-me", "/industries", "/guides"],
    publicationStatus: "published"
  },
  {
    route: "/for",
    pageType: "core",
    title: "Audience Directory: Solutions for Job Seekers, Recruiters & Sales | BizNearYou",
    metaDescription: "Explore BizNearYou solutions tailored for Job Seekers, Recruiters, Staffing Agencies, Career Coaches, Workforce Boards, Career Centers, Sales Teams, and Agencies.",
    canonicalUrl: `${PRIMARY_DOMAIN}/for`,
    h1: "Solutions Tailored for Every Professional Search Intent",
    breadcrumbLabel: "Audience Directory",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-08-09",
    structuredDataType: "WebPage",
    relatedRoutes: ["/for-job-seekers", "/for-recruiters", "/for-staffing-agencies", "/for-career-coaches", "/for-workforce-organizations", "/for-college-career-centers", "/for-sales-teams", "/for-agencies"],
    publicationStatus: "published"
  },
  {
    route: "/find-jobs",
    pageType: "core",
    title: "Find Employers & Local Business Directory | BizNearYou",
    metaDescription: "Search independent local businesses across healthcare, trades, retail, and technology. Map potential employers within your commute radius.",
    canonicalUrl: `${PRIMARY_DOMAIN}/find-jobs`,
    h1: "Search Local Employers & Businesses Near You",
    breadcrumbLabel: "Find Employers",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "WebPage",
    relatedRoutes: ["/hidden-jobs", "/businesses-hiring-near-me", "/locations", "/industries"],
    publicationStatus: "published"
  },
  {
    route: "/hidden-jobs",
    pageType: "core",
    title: "Uncover Hidden Jobs & Direct Employer Outreach | BizNearYou",
    metaDescription: "Access unposted local job opportunities. Learn how to connect with local business owners directly.",
    canonicalUrl: `${PRIMARY_DOMAIN}/hidden-jobs`,
    h1: "Access the Local Hidden Job Market",
    breadcrumbLabel: "Hidden Jobs",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "WebPage",
    relatedRoutes: ["/guides/how-to-find-hidden-jobs", "/features/hiring-signals"],
    publicationStatus: "published"
  },
  {
    route: "/local-internships",
    pageType: "core",
    title: "Find Local Internships at Small Businesses | BizNearYou",
    metaDescription: "Discover student internship opportunities at independent local medical practices, law firms, trade shops, and tech agencies.",
    canonicalUrl: `${PRIMARY_DOMAIN}/local-internships`,
    h1: "Local Student Internships & Entry-Level Opportunities",
    breadcrumbLabel: "Local Internships",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "WebPage",
    relatedRoutes: ["/first-jobs", "/guides/how-to-find-an-internship-near-you"],
    publicationStatus: "published"
  },
  {
    route: "/first-jobs",
    pageType: "core",
    title: "First Jobs & Entry-Level Employer Directory | BizNearYou",
    metaDescription: "Find entry-level roles requiring zero prior experience at local small businesses. Get hired directly by local owners.",
    canonicalUrl: `${PRIMARY_DOMAIN}/first-jobs`,
    h1: "First Jobs & Entry-Level Positions Near You",
    breadcrumbLabel: "First Jobs",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "WebPage",
    relatedRoutes: ["/guides/how-to-find-a-job-with-no-experience", "/guides/first-resume-with-no-experience"],
    publicationStatus: "published"
  },
  {
    route: "/how-signals-work",
    pageType: "core",
    title: "How Signals Work: Verification & Conservative Standards | BizNearYou",
    metaDescription: "Learn how BizNearYou detects public hiring and growth signals, why we use conservative evidence labels, and how freshness dates help you verify local opportunities.",
    canonicalUrl: `${PRIMARY_DOMAIN}/how-signals-work`,
    h1: "How BizNearYou Signals Work: Evidence & Verification Standards",
    breadcrumbLabel: "How Signals Work",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-30",
    structuredDataType: "WebPage",
    relatedRoutes: ["/businesses-hiring-near-me", "/hiring-signal-methodology", "/features/hiring-signals", "/for-job-seekers"],
    publicationStatus: "published"
  },
  {
    route: "/businesses-hiring-near-me",
    pageType: "core",
    title: "Businesses Hiring Near Me & Growth Signals | BizNearYou",
    metaDescription: "Identify local employers showing active growth signals, review surges, and facility expansions in your neighborhood.",
    canonicalUrl: `${PRIMARY_DOMAIN}/businesses-hiring-near-me`,
    h1: "Local Businesses Displaying Hiring & Growth Signals",
    breadcrumbLabel: "Hiring Signals",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "WebPage",
    relatedRoutes: ["/features/hiring-signals", "/methodology/hiring-signals"],
    publicationStatus: "published"
  },
  {
    route: "/for-job-seekers",
    pageType: "core",
    title: "Find Nearby Employers and Hiring Opportunities | BizNearYou",
    metaDescription: "Explore local businesses, review hiring signals, and find ways to contact employers directly — even when they don’t have a public job listing.",
    canonicalUrl: `${PRIMARY_DOMAIN}/for-job-seekers`,
    h1: "Find Nearby Employers and Hiring Opportunities",
    breadcrumbLabel: "For Job Seekers",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-26",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/find-jobs", "/hidden-jobs", "/businesses-hiring-near-me", "/how-it-works"],
    publicationStatus: "published"
  },
  {
    route: "/for-recruiters",
    pageType: "core",
    title: "BizNearYou for Recruiters | Spot Hiring Companies & Recruiting Prospects",
    metaDescription: "Identify growing local businesses with active hiring signals. Source prospective client accounts, track recruitment needs, and win new recruiting business.",
    canonicalUrl: `${PRIMARY_DOMAIN}/for-recruiters`,
    h1: "Source Employer Accounts Showing Active Hiring Demand",
    breadcrumbLabel: "For Recruiters",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-26",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/recruiter-business-search", "/employer-prospecting", "/business-hiring-signals", "/pricing"],
    publicationStatus: "published"
  },
  {
    route: "/for-staffing-agencies",
    pageType: "core",
    title: "BizNearYou for Staffing Agencies | Target High-Volume & Temporary Staffing Prospects",
    metaDescription: "Locate local employers facing seasonal demand spikes, high turnover, or multi-shift staffing needs. Generate qualified commercial staffing leads.",
    canonicalUrl: `${PRIMARY_DOMAIN}/for-staffing-agencies`,
    h1: "Target Employers Needing Temporary & High-Volume Staffing",
    breadcrumbLabel: "For Staffing Agencies",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-26",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/staffing-prospecting", "/employer-prospecting", "/local-business-prospecting", "/pricing"],
    publicationStatus: "published"
  },
  {
    route: "/for-career-coaches",
    pageType: "core",
    title: "BizNearYou for Career Coaches | Curate Employer Target Lists for Clients",
    metaDescription: "Research local employers, curate client-specific opportunity workspaces, and track application progress for your job seeking coaching clients.",
    canonicalUrl: `${PRIMARY_DOMAIN}/for-career-coaches`,
    h1: "Curate Target Employer Lists & Hidden Job Leads for Clients",
    breadcrumbLabel: "For Career Coaches",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-26",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/for-job-seekers", "/for-college-career-centers", "/how-it-works", "/pricing"],
    publicationStatus: "published"
  },
  {
    route: "/for-workforce-organizations",
    pageType: "core",
    title: "BizNearYou for Workforce Organizations | Map Regional Hiring Signals",
    metaDescription: "Empower workforce development boards and community employment programs with regional business intelligence, employer engagement CRM, and placement tools.",
    canonicalUrl: `${PRIMARY_DOMAIN}/for-workforce-organizations`,
    h1: "Map Regional Employer Hiring Signals & Community Job Data",
    breadcrumbLabel: "For Workforce Orgs",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-26",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/for-college-career-centers", "/business-hiring-signals", "/data-sources", "/pricing"],
    publicationStatus: "published"
  },
  {
    route: "/for-college-career-centers",
    pageType: "core",
    title: "BizNearYou for College Career Centers | Student Internship & Entry-Level Employer Directory",
    metaDescription: "Connect students and recent graduates with local small businesses, clinics, trade shops, and agencies offering entry-level roles and internships.",
    canonicalUrl: `${PRIMARY_DOMAIN}/for-college-career-centers`,
    h1: "Connect Students with Nearby Entry-Level & Internship Employers",
    breadcrumbLabel: "For Career Centers",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-26",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/local-internships", "/first-jobs", "/for-workforce-organizations", "/pricing"],
    publicationStatus: "published"
  },
  {
    route: "/for-sales-teams",
    pageType: "core",
    title: "BizNearYou for Sales Teams | B2B Local Business Prospecting Tool",
    metaDescription: "Discover local commercial businesses needing tech, web, or commercial services. Analyze web presence, detect tech gaps, and reach owners.",
    canonicalUrl: `${PRIMARY_DOMAIN}/for-sales-teams`,
    h1: "Discover Qualified B2B Sales Prospects & Service Leads",
    breadcrumbLabel: "For Sales Teams",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-26",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/local-business-prospecting", "/for-agencies", "/for-business-development", "/pricing"],
    publicationStatus: "published"
  },
  {
    route: "/for-business-development",
    pageType: "core",
    title: "BizNearYou for Business Development | Strategic Partnership Prospecting",
    metaDescription: "Identify multi-location regional companies, potential referral partners, and market expansion targets using business intelligence.",
    canonicalUrl: `${PRIMARY_DOMAIN}/for-business-development`,
    h1: "Identify Strategic Partners & Regional Expansion Targets",
    breadcrumbLabel: "For Business Dev",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-26",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/for-sales-teams", "/local-business-prospecting", "/data-sources", "/pricing"],
    publicationStatus: "published"
  },
  {
    route: "/for-agencies",
    pageType: "core",
    title: "BizNearYou for Agencies & Freelancers | Find Local Web & SEO Clients",
    metaDescription: "Scan local small businesses for missing websites, SEO flaws, and technology gaps. Win agency web design, SEO, and digital marketing clients.",
    canonicalUrl: `${PRIMARY_DOMAIN}/for-agencies`,
    h1: "Find Local Businesses Needing Web Design, SEO, & Marketing",
    breadcrumbLabel: "For Agencies",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-26",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/local-business-prospecting", "/for-sales-teams", "/hiring-signal-methodology", "/pricing"],
    publicationStatus: "published"
  },
  {
    route: "/business-hiring-signals",
    pageType: "core",
    title: "Business Hiring Signals Engine | Real-Time Employer Growth Intelligence",
    metaDescription: "Explore how public review surges, location expansions, and web activity signal active recruitment demand across local businesses.",
    canonicalUrl: `${PRIMARY_DOMAIN}/business-hiring-signals`,
    h1: "Understanding Business Hiring Signals & Growth Intelligence",
    breadcrumbLabel: "Hiring Signals Engine",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-26",
    structuredDataType: "WebPage",
    relatedRoutes: ["/hiring-signal-methodology", "/businesses-hiring-near-me", "/for-recruiters"],
    publicationStatus: "published"
  },
  {
    route: "/local-business-prospecting",
    pageType: "core",
    title: "Local Business Prospecting Software | BizNearYou Opportunity Engine",
    metaDescription: "Target commercial local businesses by category, location count, web presence, and growth indicators for B2B sales and agency outreach.",
    canonicalUrl: `${PRIMARY_DOMAIN}/local-business-prospecting`,
    h1: "Local Business Prospecting & Commercial Lead Intelligence",
    breadcrumbLabel: "Business Prospecting",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-26",
    structuredDataType: "WebPage",
    relatedRoutes: ["/for-sales-teams", "/for-agencies", "/employer-prospecting"],
    publicationStatus: "published"
  },
  {
    route: "/employer-prospecting",
    pageType: "core",
    title: "Employer Prospecting for Recruiters & Staffing Agencies | BizNearYou",
    metaDescription: "Prospect potential employer clients by analyzing hiring evidence, headcount growth indicators, and regional business presence.",
    canonicalUrl: `${PRIMARY_DOMAIN}/employer-prospecting`,
    h1: "Employer Prospecting Engine for Recruitment & Staffing",
    breadcrumbLabel: "Employer Prospecting",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-26",
    structuredDataType: "WebPage",
    relatedRoutes: ["/for-recruiters", "/for-staffing-agencies", "/recruiter-business-search"],
    publicationStatus: "published"
  },
  {
    route: "/staffing-prospecting",
    pageType: "core",
    title: "Staffing Agency Prospecting & Demand Intelligence | BizNearYou",
    metaDescription: "Discover local companies with high turnover, multi-shift demands, or seasonal hiring spikes requiring staffing agency assistance.",
    canonicalUrl: `${PRIMARY_DOMAIN}/staffing-prospecting`,
    h1: "Commercial Staffing Prospecting & Lead Generation",
    breadcrumbLabel: "Staffing Prospecting",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-26",
    structuredDataType: "WebPage",
    relatedRoutes: ["/for-staffing-agencies", "/employer-prospecting", "/pricing"],
    publicationStatus: "published"
  },
  {
    route: "/recruiter-business-search",
    pageType: "core",
    title: "Recruiter Business Search Tool | Find Hiring Companies Near You",
    metaDescription: "Search independent businesses by hiring signal strength, recency, and category to build targeted recruitment prospect lists.",
    canonicalUrl: `${PRIMARY_DOMAIN}/recruiter-business-search`,
    h1: "Recruiter Business Search & Employer Growth Tracker",
    breadcrumbLabel: "Recruiter Search",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-26",
    structuredDataType: "WebPage",
    relatedRoutes: ["/for-recruiters", "/employer-prospecting", "/pricing"],
    publicationStatus: "published"
  },
  {
    route: "/hiring-signal-methodology",
    pageType: "core",
    title: "Hiring Signal Methodology & Detection Science | BizNearYou",
    metaDescription: "In-depth scientific breakdown of direct vs inferred hiring signals, confidence scoring, recency decay, and false-positive prevention.",
    canonicalUrl: `${PRIMARY_DOMAIN}/hiring-signal-methodology`,
    h1: "Hiring Signal Methodology & Detection Science",
    breadcrumbLabel: "Hiring Methodology",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-26",
    structuredDataType: "TechArticle",
    relatedRoutes: ["/opportunity-score-methodology", "/data-sources", "/accuracy-and-limitations"],
    publicationStatus: "published"
  },
  {
    route: "/hidden-jobs-near-me",
    pageType: "core",
    title: "Find Hidden Jobs Near You Before They Are Posted Online | BizNearYou",
    metaDescription: "Uncover unadvertised job opportunities at local small businesses, clinics, and offices in your area. Reach decision-makers directly with verified contact signals.",
    canonicalUrl: `${PRIMARY_DOMAIN}/hidden-jobs-near-me`,
    h1: "Find Hidden Jobs Near You Before They Are Posted Online",
    breadcrumbLabel: "Hidden Jobs Near Me",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-30",
    structuredDataType: "WebPage",
    relatedRoutes: ["/hidden-jobs", "/businesses-hiring-near-me", "/companies-hiring-without-job-postings"],
    publicationStatus: "published"
  },
  {
    route: "/local-businesses-hiring",
    pageType: "core",
    title: "Local Businesses Hiring Near You | Growth & Expansion Signals | BizNearYou",
    metaDescription: "Search independent local businesses displaying active hiring demand, customer review surges, and facility expansions in your neighborhood.",
    canonicalUrl: `${PRIMARY_DOMAIN}/local-businesses-hiring`,
    h1: "Local Businesses Displaying Hiring & Expansion Signals",
    breadcrumbLabel: "Local Businesses Hiring",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-30",
    structuredDataType: "WebPage",
    relatedRoutes: ["/businesses-hiring-near-me", "/small-businesses-hiring-near-me", "/find-jobs"],
    publicationStatus: "published"
  },
  {
    route: "/companies-hiring-without-job-postings",
    pageType: "core",
    title: "Companies Hiring Without Job Postings | Direct Outreach Finder | BizNearYou",
    metaDescription: "Discover commercially active companies that need help but haven't published job ads on major boards. Pitch local owners directly with confidence.",
    canonicalUrl: `${PRIMARY_DOMAIN}/companies-hiring-without-job-postings`,
    h1: "Find Companies Hiring Without Public Job Postings",
    breadcrumbLabel: "Unadvertised Hiring",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-30",
    structuredDataType: "WebPage",
    relatedRoutes: ["/hidden-jobs", "/hidden-jobs-near-me", "/for-job-seekers"],
    publicationStatus: "published"
  },
  {
    route: "/small-businesses-hiring-near-me",
    pageType: "core",
    title: "Small Businesses Hiring Near Me | Local Employers Directory | BizNearYou",
    metaDescription: "Identify local small businesses, medical practices, trade contractors, and boutique agencies showing hiring demand. Direct outreach CRM for job seekers.",
    canonicalUrl: `${PRIMARY_DOMAIN}/small-businesses-hiring-near-me`,
    h1: "Small Businesses Displaying Hiring Signals Near You",
    breadcrumbLabel: "Small Businesses Hiring",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-30",
    structuredDataType: "WebPage",
    relatedRoutes: ["/local-businesses-hiring", "/businesses-hiring-near-me", "/first-jobs"],
    publicationStatus: "published"
  },
  {
    route: "/local-job-search-tool",
    pageType: "core",
    title: "Local Job Search Tool & Employer CRM | BizNearYou Opportunity Finder",
    metaDescription: "Use interactive map filters, hiring signal indicators, and outreach script generators to streamline your local job search campaign.",
    canonicalUrl: `${PRIMARY_DOMAIN}/local-job-search-tool`,
    h1: "Local Job Search Tool & Direct Outreach CRM",
    breadcrumbLabel: "Job Search Tool",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-30",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/find-jobs", "/business-opportunity-finder", "/for-job-seekers"],
    publicationStatus: "published"
  },
  {
    route: "/business-opportunity-finder",
    pageType: "core",
    title: "Business Opportunity Finder & Local Market Intelligence | BizNearYou",
    metaDescription: "Spot unadvertised hiring signals, commercial growth indicators, and service gaps across local businesses in your commute radius.",
    canonicalUrl: `${PRIMARY_DOMAIN}/business-opportunity-finder`,
    h1: "Local Business Opportunity Finder & Growth Tracker",
    breadcrumbLabel: "Opportunity Finder",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-30",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/local-job-search-tool", "/local-lead-finder", "/business-explorer"],
    publicationStatus: "published"
  },
  {
    route: "/local-lead-finder",
    pageType: "core",
    title: "Local Business Lead Finder | B2B & Commercial Prospecting | BizNearYou",
    metaDescription: "Search local commercial entities by category, web presence, review velocity, and hiring indicators to build targeted B2B prospect lists.",
    canonicalUrl: `${PRIMARY_DOMAIN}/local-lead-finder`,
    h1: "Local Business Lead Finder & Prospect Sourcing",
    breadcrumbLabel: "Lead Finder",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-30",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/sales-prospecting-local-businesses", "/agency-client-finder", "/for-sales-teams"],
    publicationStatus: "published"
  },
  {
    route: "/staffing-agency-leads",
    pageType: "core",
    title: "Staffing Agency Lead Generation Tool | Target Hiring Demand | BizNearYou",
    metaDescription: "Locate local employers facing shift coverage needs, high turnover, or seasonal spikes requiring temporary or commercial staffing assistance.",
    canonicalUrl: `${PRIMARY_DOMAIN}/staffing-agency-leads`,
    h1: "Staffing Agency Commercial Lead Generation Tool",
    breadcrumbLabel: "Staffing Leads",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-30",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/for-staffing-agencies", "/staffing-prospecting", "/employer-prospecting"],
    publicationStatus: "published"
  },
  {
    route: "/career-coach-job-search-tool",
    pageType: "core",
    title: "Career Coach Job Search Tool | Employer Target Lists for Clients | BizNearYou",
    metaDescription: "Curate tailored local employer target lists, track application progress, and empower job-seeking coaching clients with proactive outreach CRM.",
    canonicalUrl: `${PRIMARY_DOMAIN}/career-coach-job-search-tool`,
    h1: "Employer Target List Creator for Career Coaches",
    breadcrumbLabel: "Career Coach Tool",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-30",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/for-career-coaches", "/for-job-seekers", "/local-job-search-tool"],
    publicationStatus: "published"
  },
  {
    route: "/workforce-development-business-search",
    pageType: "core",
    title: "Workforce Development Business Search | Regional Hiring Intelligence | BizNearYou",
    metaDescription: "Map regional employer demand, support workforce development boards, and connect job seekers with active community employers.",
    canonicalUrl: `${PRIMARY_DOMAIN}/workforce-development-business-search`,
    h1: "Regional Employer Intelligence for Workforce Development",
    breadcrumbLabel: "Workforce Dev Search",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-30",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/for-workforce-organizations", "/college-career-center-employer-search", "/business-hiring-signals"],
    publicationStatus: "published"
  },
  {
    route: "/college-career-center-employer-search",
    pageType: "core",
    title: "College Career Center Employer Search | Internships & Entry-Level Roles | BizNearYou",
    metaDescription: "Connect students and recent graduates with local small businesses, medical practices, trade shops, and creative agencies offering early-career roles.",
    canonicalUrl: `${PRIMARY_DOMAIN}/college-career-center-employer-search`,
    h1: "Student Internship & Entry-Level Employer Directory",
    breadcrumbLabel: "Career Center Search",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-30",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/for-college-career-centers", "/local-internships", "/first-jobs"],
    publicationStatus: "published"
  },
  {
    route: "/sales-prospecting-local-businesses",
    pageType: "core",
    title: "Sales Prospecting for Local Businesses | B2B Commercial Leads | BizNearYou",
    metaDescription: "Identify qualified local commercial prospects, analyze technology gaps and web presence, and reach decision-makers for B2B services.",
    canonicalUrl: `${PRIMARY_DOMAIN}/sales-prospecting-local-businesses`,
    h1: "B2B Sales Prospecting for Local Commercial Businesses",
    breadcrumbLabel: "Sales Prospecting",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-30",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/for-sales-teams", "/local-lead-finder", "/agency-client-finder"],
    publicationStatus: "published"
  },
  {
    route: "/business-development-local-opportunities",
    pageType: "core",
    title: "Business Development Tool | Find Strategic Regional Partners | BizNearYou",
    metaDescription: "Target multi-location regional companies, referral partners, and market expansion opportunities with business intelligence.",
    canonicalUrl: `${PRIMARY_DOMAIN}/business-development-local-opportunities`,
    h1: "Regional Business Development & Strategic Opportunity Search",
    breadcrumbLabel: "BizDev Opportunities",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-30",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/for-business-development", "/sales-prospecting-local-businesses", "/local-lead-finder"],
    publicationStatus: "published"
  },
  {
    route: "/agency-client-finder",
    pageType: "core",
    title: "Agency Client Finder | Local Web Design, SEO & Marketing Prospects | BizNearYou",
    metaDescription: "Discover local small businesses with missing websites, outdated tech, or low search visibility to pitch web development and marketing services.",
    canonicalUrl: `${PRIMARY_DOMAIN}/agency-client-finder`,
    h1: "Local Business Client Finder for Digital Agencies & Freelancers",
    breadcrumbLabel: "Agency Client Finder",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-30",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/for-agencies", "/local-lead-finder", "/sales-prospecting-local-businesses"],
    publicationStatus: "published"
  },
  {
    route: "/business-explorer",
    pageType: "core",
    title: "Local Business Explorer & Commercial Density Benchmark | BizNearYou",
    metaDescription: "Explore local business distribution across categories, evaluate market density, and track commercial growth indicators in any city.",
    canonicalUrl: `${PRIMARY_DOMAIN}/business-explorer`,
    h1: "Local Business Explorer & Market Density Benchmark",
    breadcrumbLabel: "Business Explorer",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-30",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/explore", "/locations", "/business-opportunity-finder"],
    publicationStatus: "published"
  },
  {
    route: "/opportunity-score-methodology",
    pageType: "core",
    title: "Opportunity Score Methodology & Multi-Audience Weighting | BizNearYou",
    metaDescription: "How the Opportunity Score adapts mathematically for Job Seekers, Recruiters, Staffing Agencies, Sales Teams, and Agencies.",
    canonicalUrl: `${PRIMARY_DOMAIN}/opportunity-score-methodology`,
    h1: "Opportunity Score Methodology & Adaptive Ranking",
    breadcrumbLabel: "Score Methodology",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-26",
    structuredDataType: "TechArticle",
    relatedRoutes: ["/hiring-signal-methodology", "/data-sources", "/accuracy-and-limitations"],
    publicationStatus: "published"
  },
  {
    route: "/data-sources",
    pageType: "core",
    title: "Public Commercial Data Sources & Verification | BizNearYou",
    metaDescription: "Complete transparency into public business filings, web registry records, location APIs, and verification pipelines powering BizNearYou.",
    canonicalUrl: `${PRIMARY_DOMAIN}/data-sources`,
    h1: "Data Sources & Commercial Verification Standards",
    breadcrumbLabel: "Data Sources",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-26",
    structuredDataType: "TechArticle",
    relatedRoutes: ["/how-data-is-collected", "/accuracy-and-limitations", "/hiring-signal-methodology"],
    publicationStatus: "published"
  },
  {
    route: "/accuracy-and-limitations",
    pageType: "core",
    title: "Data Accuracy, Signal Confidence, & Product Limitations | BizNearYou",
    metaDescription: "Understand confidence levels, signal uncertainty, data freshness guidelines, and why BizNearYou never guarantees unposted job openings.",
    canonicalUrl: `${PRIMARY_DOMAIN}/accuracy-and-limitations`,
    h1: "Accuracy Standards, Confidence Levels, & Product Limitations",
    breadcrumbLabel: "Accuracy & Limits",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-26",
    structuredDataType: "TechArticle",
    relatedRoutes: ["/data-sources", "/hiring-signal-methodology", "/opportunity-score-methodology"],
    publicationStatus: "published"
  },
  {
    route: "/how-it-works",
    pageType: "core",
    title: "How BizNearYou Works - Local Business Discovery Guide",
    metaDescription: "Learn how BizNearYou maps local small businesses, calculates Opportunity Scores, and empowers direct employer outreach.",
    canonicalUrl: `${PRIMARY_DOMAIN}/how-it-works`,
    h1: "How BizNearYou Helps You Connect with Local Employers",
    breadcrumbLabel: "How It Works",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-24",
    structuredDataType: "WebPage",
    relatedRoutes: ["/features", "/methodology/hiring-signals", "/why-biznearyou"],
    publicationStatus: "published"
  },
  {
    route: "/why-biznearyou",
    pageType: "core",
    title: "Why Choose BizNearYou | Local Business Discovery Engine",
    metaDescription: "Discover why job seekers, trade specialists, students, and career switchers choose BizNearYou over national ad boards.",
    canonicalUrl: `${PRIMARY_DOMAIN}/why-biznearyou`,
    h1: "Why BizNearYou is Built for Local Business Discovery",
    breadcrumbLabel: "Why BizNearYou",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-24",
    structuredDataType: "WebPage",
    relatedRoutes: ["/how-it-works", "/about", "/compare/biznearyou-vs-traditional-job-boards"],
    publicationStatus: "published"
  },
  {
    route: "/faq",
    pageType: "core",
    title: "Frequently Asked Questions (FAQ) | BizNearYou",
    metaDescription: "Get clear, direct answers about BizNearYou local business discovery, Opportunity Scores, Hiring Signals, and direct outreach.",
    canonicalUrl: `${PRIMARY_DOMAIN}/faq`,
    h1: "Frequently Asked Questions About BizNearYou",
    breadcrumbLabel: "FAQ",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-24",
    structuredDataType: "FAQPage",
    relatedRoutes: ["/how-it-works", "/how-data-is-collected", "/how-scores-are-calculated"],
    publicationStatus: "published"
  },
  {
    route: "/how-data-is-collected",
    pageType: "core",
    title: "How Business Data Is Collected & Verified | BizNearYou",
    metaDescription: "Transparent breakdown of public APIs, state business filings, domain WHOIS records, and verification safeguards powering BizNearYou.",
    canonicalUrl: `${PRIMARY_DOMAIN}/how-data-is-collected`,
    h1: "How BizNearYou Aggregates & Verifies Public Commercial Data",
    breadcrumbLabel: "Data Sources",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-24",
    structuredDataType: "TechArticle",
    relatedRoutes: ["/privacy", "/how-scores-are-calculated", "/about"],
    publicationStatus: "published"
  },
  {
    route: "/how-scores-are-calculated",
    pageType: "core",
    title: "How Opportunity Scores Are Calculated | BizNearYou",
    metaDescription: "Detailed mathematical breakdown of the 1-100 Opportunity Score algorithm evaluating employer contactability and stability.",
    canonicalUrl: `${PRIMARY_DOMAIN}/how-scores-are-calculated`,
    h1: "Understanding the Opportunity Score Algorithm",
    breadcrumbLabel: "Score Calculation",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-24",
    structuredDataType: "TechArticle",
    relatedRoutes: ["/features/opportunity-score", "/how-hiring-signals-work"],
    publicationStatus: "published"
  },
  {
    route: "/how-hiring-signals-work",
    pageType: "core",
    title: "How Hiring Signals Work | BizNearYou Methodology",
    metaDescription: "Learn how public growth indicators like review momentum and web service expansions reveal hiring activity.",
    canonicalUrl: `${PRIMARY_DOMAIN}/how-hiring-signals-work`,
    h1: "How Hiring Signals Identify Expanding Local Employers",
    breadcrumbLabel: "Hiring Signals Method",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-24",
    structuredDataType: "TechArticle",
    relatedRoutes: ["/features/hiring-signals", "/businesses-hiring-near-me"],
    publicationStatus: "published"
  },
  {
    route: "/why-businesses-before-job-listings",
    pageType: "core",
    title: "Why Businesses Appear Before Job Listings | Unposted Hiring Overview | BizNearYou",
    metaDescription: "Overview of why local small business hiring often occurs without public job postings and how mapping local businesses unlocks hidden job openings.",
    canonicalUrl: `${PRIMARY_DOMAIN}/why-businesses-before-job-listings`,
    h1: "Why BizNearYou Maps Businesses Before Jobs Are Posted",
    breadcrumbLabel: "Unposted Opportunities",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-24",
    structuredDataType: "Article",
    relatedRoutes: ["/hidden-jobs", "/why-biznearyou"],
    publicationStatus: "published"
  },
  {
    route: "/how-to-contact-local-businesses-for-a-job",
    pageType: "core",
    title: "How to Contact Local Businesses for a Job | BizNearYou",
    metaDescription: "Step-by-step methodology for emailing, calling, or visiting local business owners directly to secure employment.",
    canonicalUrl: `${PRIMARY_DOMAIN}/how-to-contact-local-businesses-for-a-job`,
    h1: "Mastering Direct Outreach to Local Employers",
    breadcrumbLabel: "Contact Guide",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "WebPage",
    relatedRoutes: ["/guides/how-to-email-a-business-about-a-job"],
    publicationStatus: "published"
  },
  {
    route: "/how-to-prioritize-businesses",
    pageType: "core",
    title: "How to Prioritize Target Employers | BizNearYou",
    metaDescription: "Use Opportunity Scores and Hiring Signals to rank target local businesses by contactability and growth trajectory.",
    canonicalUrl: `${PRIMARY_DOMAIN}/how-to-prioritize-businesses`,
    h1: "Prioritizing Your Target Employer List",
    breadcrumbLabel: "Prioritization Guide",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "WebPage",
    relatedRoutes: ["/features/opportunity-score"],
    publicationStatus: "published"
  },
  {
    route: "/how-to-organize-local-job-search",
    pageType: "core",
    title: "How to Organize Your Local Job Search | BizNearYou",
    metaDescription: "Structure your local job hunt like a CRM. Track contacts, dates, and follow-up reminders.",
    canonicalUrl: `${PRIMARY_DOMAIN}/how-to-organize-local-job-search`,
    h1: "Organizing an Actionable Local Job Search",
    breadcrumbLabel: "Organization Guide",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "WebPage",
    relatedRoutes: ["/features/crm-pipeline", "/guides/how-to-organize-a-local-job-search"],
    publicationStatus: "published"
  },
  {
    route: "/how-to-ask-if-hiring",
    pageType: "core",
    title: "How to Ask if a Business is Hiring | BizNearYou",
    metaDescription: "Practical scripts and conversation starters for asking small business owners about job opportunities.",
    canonicalUrl: `${PRIMARY_DOMAIN}/guides/how-to-ask-if-a-business-is-hiring`,
    h1: "How to Ask Local Businesses About Openings",
    breadcrumbLabel: "Inquiry Guide",
    sitemapInclusion: false,
    indexingStatus: "noindex, follow",
    lastmod: "2026-07-23",
    structuredDataType: "WebPage",
    relatedRoutes: ["/guides/how-to-ask-if-a-business-is-hiring"],
    publicationStatus: "published"
  },
  {
    route: "/hiring-signals-explained",
    pageType: "core",
    title: "Hiring Signals Explained - Commercial Growth Metrics",
    metaDescription: "Understand what review spikes, expansion announcements, and new service lines reveal about employer hiring needs.",
    canonicalUrl: `${PRIMARY_DOMAIN}/hiring-signals-explained`,
    h1: "Understanding Local Hiring Signals",
    breadcrumbLabel: "Hiring Signals Guide",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "WebPage",
    relatedRoutes: ["/methodology/hiring-signals", "/features/hiring-signals"],
    publicationStatus: "published"
  },
  {
    route: "/explore",
    pageType: "explore-hub",
    title: "Explore Local Job-Search Resources | BizNearYou",
    metaDescription: "Central directory for local job search locations, hiring businesses, student internships, career guides, industry trends, product features, and research reports.",
    canonicalUrl: `${PRIMARY_DOMAIN}/explore`,
    h1: "Explore BizNearYou Resources",
    breadcrumbLabel: "Explore",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "WebSite",
    relatedRoutes: ["/locations", "/jobs/locations", "/businesses-hiring/locations", "/internships/locations", "/guides", "/industries", "/features"],
    publicationStatus: "published"
  },
  {
    route: "/locations",
    pageType: "location-directory",
    title: "City & Regional Directory of Local Employers | BizNearYou",
    metaDescription: "Explore verified local businesses, commercial hubs, and city employer directories across all supported regions.",
    canonicalUrl: `${PRIMARY_DOMAIN}/locations`,
    h1: "Local Employer Directories by City & Region",
    breadcrumbLabel: "Locations",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "ItemList",
    relatedRoutes: ["/jobs/locations", "/businesses-hiring/locations", "/internships/locations"],
    publicationStatus: "published"
  },
  {
    route: "/jobs/locations",
    pageType: "location-directory",
    title: "Browse Local Job Search Locations | BizNearYou",
    metaDescription: "Explore supported cities and regional hubs across the US to discover local employers, commercial activity signals, and unadvertised job opportunities.",
    canonicalUrl: `${PRIMARY_DOMAIN}/jobs/locations`,
    h1: "Supported Job Search Locations & Regional Hubs",
    breadcrumbLabel: "Jobs Locations",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "ItemList",
    relatedRoutes: ["/businesses-hiring/locations", "/internships/locations", "/locations"],
    publicationStatus: "published"
  },
  {
    route: "/businesses-hiring/locations",
    pageType: "location-directory",
    title: "Browse Cities with Hiring Signal Directory | BizNearYou",
    metaDescription: "Find cities with active commercial growth, expanding medical offices, bustling contractors, and hiring signals worth investigating.",
    canonicalUrl: `${PRIMARY_DOMAIN}/businesses-hiring/locations`,
    h1: "Cities with Active Commercial Hiring Signals",
    breadcrumbLabel: "Hiring Signal Locations",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "ItemList",
    relatedRoutes: ["/jobs/locations", "/internships/locations", "/locations"],
    publicationStatus: "published"
  },
  {
    route: "/internships/locations",
    pageType: "location-directory",
    title: "Browse Student Internship Locations Directory | BizNearYou",
    metaDescription: "Discover cities and communities with private practices, clinics, law offices, and local businesses worth pitching for student internships.",
    canonicalUrl: `${PRIMARY_DOMAIN}/internships/locations`,
    h1: "Student & Early-Career Internship Locations",
    breadcrumbLabel: "Internship Locations",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "ItemList",
    relatedRoutes: ["/jobs/locations", "/businesses-hiring/locations", "/locations"],
    publicationStatus: "published"
  },
  {
    route: "/industries",
    pageType: "industry-directory",
    title: "Local Business Industry Directory | BizNearYou",
    metaDescription: "Browse local independent employers, outpatient clinics, trade contractors, specialty merchants, and law practices across industries.",
    canonicalUrl: `${PRIMARY_DOMAIN}/industries`,
    h1: "Discover Local Employers by Industry",
    breadcrumbLabel: "Industries",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "ItemList",
    relatedRoutes: ["/industries/healthcare-medical", "/industries/construction-contractors", "/industries/retail-specialty"],
    publicationStatus: "published"
  },
  {
    route: "/guides",
    pageType: "guide-hub",
    title: "Career & Direct Outreach Guide Hub | BizNearYou",
    metaDescription: "Actionable guides for local job searching, cold emailing small business owners, landing entry-level jobs without experience, and finding hidden opportunities.",
    canonicalUrl: `${PRIMARY_DOMAIN}/guides`,
    h1: "Local Job Search & Direct Outreach Guides",
    breadcrumbLabel: "Guides",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "ItemList",
    relatedRoutes: ["/guides/how-to-ask-if-a-business-is-hiring", "/guides/how-to-find-a-job-with-no-experience"],
    publicationStatus: "published"
  },
  {
    route: "/features",
    pageType: "feature-hub",
    title: "BizNearYou Features & Tools Overview",
    metaDescription: "Explore BizNearYou features including Local Business Discovery, Hiring Signals, Opportunity Scores, Contact Search, Website Audits, and CRM Pipeline.",
    canonicalUrl: `${PRIMARY_DOMAIN}/features`,
    h1: "BizNearYou Discovery Features & Pro Tools",
    breadcrumbLabel: "Features",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "SoftwareApplication",
    relatedRoutes: ["/features/map-based-employer-search", "/features/hiring-likelihood-scoring"],
    publicationStatus: "published"
  },
  {
    route: "/reports",
    pageType: "report-hub",
    title: "Local Employer & Market Data Reports | BizNearYou",
    metaDescription: "Data-driven reports analyzing local small business density, trade contractor momentum, contactability benchmarks, and digital presence quality.",
    canonicalUrl: `${PRIMARY_DOMAIN}/reports`,
    h1: "Local Small Employer Data Reports & Trends",
    breadcrumbLabel: "Reports",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "ItemList",
    relatedRoutes: ["/reports/peoria-il-local-opportunity-report", "/reports/illinois-local-employer-trends"],
    publicationStatus: "published"
  },
  {
    route: "/compare",
    pageType: "compare-hub",
    title: "BizNearYou Comparisons & Search Methodologies",
    metaDescription: "Objective comparisons between BizNearYou local business discovery, traditional job boards, and direct employer outreach strategies.",
    canonicalUrl: `${PRIMARY_DOMAIN}/compare`,
    h1: "Comparing Local Business Search Methods",
    breadcrumbLabel: "Compare",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "ItemList",
    relatedRoutes: ["/compare/biznearyou-vs-traditional-job-boards"],
    publicationStatus: "published"
  },
  {
    route: "/docs",
    pageType: "core",
    title: "Documentation & Platform Knowledge Base | BizNearYou",
    metaDescription: "Complete documentation and knowledge base for BizNearYou local business discovery, CRM pipeline management, and outreach tools.",
    canonicalUrl: `${PRIMARY_DOMAIN}/docs`,
    h1: "BizNearYou Documentation & Knowledge Base",
    breadcrumbLabel: "Docs",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "WebPage",
    relatedRoutes: ["/how-it-works", "/features"],
    publicationStatus: "published"
  },
  {
    route: "/about",
    pageType: "core",
    title: "About BizNearYou - Empowering Local Job Seekers",
    metaDescription: "Learn about BizNearYou's mission to connect job seekers directly with local small business owners and unadvertised job opportunities.",
    canonicalUrl: `${PRIMARY_DOMAIN}/about`,
    h1: "About BizNearYou",
    breadcrumbLabel: "About",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "Organization",
    relatedRoutes: ["/contact", "/methodology/hiring-signals"],
    publicationStatus: "published"
  },
  {
    route: "/contact",
    pageType: "core",
    title: "Contact BizNearYou Support & Feedback",
    metaDescription: "Contact the BizNearYou team for platform support, business removal requests, press inquiries, or feature suggestions.",
    canonicalUrl: `${PRIMARY_DOMAIN}/contact`,
    h1: "Contact BizNearYou",
    breadcrumbLabel: "Contact",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "WebPage",
    relatedRoutes: ["/about", "/privacy"],
    publicationStatus: "published"
  },
  {
    route: "/pricing",
    pageType: "core",
    title: "Pricing & Plans - Free vs BizNearYou Pro",
    metaDescription: "Compare BizNearYou Free vs Pro plans. Discover expanded search limits, contact crawlers, SEO health audits, CSV export, and route optimization.",
    canonicalUrl: `${PRIMARY_DOMAIN}/pricing`,
    h1: "BizNearYou Pricing & Subscription Plans",
    breadcrumbLabel: "Pricing",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-25",
    structuredDataType: "WebPage",
    relatedRoutes: ["/how-it-works", "/features", "/why-biznearyou"],
    publicationStatus: "published"
  },
  {
    route: "/privacy",
    pageType: "legal",
    title: "Privacy Policy | BizNearYou",
    metaDescription: "Read BizNearYou's privacy policy explaining how data is collected, stored, and protected with strict user privacy safeguards.",
    canonicalUrl: `${PRIMARY_DOMAIN}/privacy`,
    h1: "Privacy Policy",
    breadcrumbLabel: "Privacy",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "WebPage",
    relatedRoutes: ["/terms", "/safety"],
    publicationStatus: "published"
  },
  {
    route: "/terms",
    pageType: "legal",
    title: "Terms of Service | BizNearYou",
    metaDescription: "Review the Terms of Service governing the use of the BizNearYou platform and local employer discovery tools.",
    canonicalUrl: `${PRIMARY_DOMAIN}/terms`,
    h1: "Terms of Service",
    breadcrumbLabel: "Terms",
    sitemapInclusion: true,
    indexingStatus: "index, follow",
    lastmod: "2026-07-23",
    structuredDataType: "WebPage",
    relatedRoutes: ["/privacy", "/safety"],
    publicationStatus: "published"
  }
];

// Helper to build dynamic SEO registry entries
export function generateFullSeoRegistry(): Record<string, SeoRegistryEntry> {
  const registry: Record<string, SeoRegistryEntry> = {};

  // 1. Core Static Pages
  CORE_PAGES.forEach((page) => {
    registry[page.route] = page;
  });

  // 2. Qualified Location Pages (Tier 2 City & Tier 3 Niche Intent Pages)
  getAllQualifiedLocations().forEach((loc) => {
    const dateStr = "2026-07-23";

    // Tier 2 City Jobs Variant
    const tier2Meta = generateTier2CityMetadata(loc, "jobs");
    const jobsRoute = `/jobs/${loc.citySlug}`;
    registry[jobsRoute] = {
      route: jobsRoute,
      pageType: "location",
      title: tier2Meta.title,
      metaDescription: tier2Meta.metaDescription,
      canonicalUrl: tier2Meta.canonicalUrl,
      h1: tier2Meta.h1,
      breadcrumbLabel: tier2Meta.breadcrumbLabel,
      sitemapInclusion: true,
      indexingStatus: "index, follow",
      lastmod: dateStr,
      structuredDataType: "Place",
      relatedRoutes: [`/businesses-hiring/${loc.citySlug}`, `/internships/${loc.citySlug}`, "/locations"],
      publicationStatus: "published"
    };

    // Tier 2 Businesses Hiring Variant
    const tier2HiringMeta = generateTier2CityMetadata(loc, "businesses-hiring");
    const hiringRoute = `/businesses-hiring/${loc.citySlug}`;
    registry[hiringRoute] = {
      route: hiringRoute,
      pageType: "location",
      title: tier2HiringMeta.title,
      metaDescription: tier2HiringMeta.metaDescription,
      canonicalUrl: tier2HiringMeta.canonicalUrl,
      h1: tier2HiringMeta.h1,
      breadcrumbLabel: `${loc.cityName}, ${loc.stateAbbreviation} Hiring Signals`,
      sitemapInclusion: true,
      indexingStatus: "index, follow",
      lastmod: dateStr,
      structuredDataType: "Place",
      relatedRoutes: [jobsRoute, `/internships/${loc.citySlug}`],
      publicationStatus: "published"
    };

    // Tier 2 Internships Variant
    const tier2InternshipsMeta = generateTier2CityMetadata(loc, "internships");
    const internshipsRoute = `/internships/${loc.citySlug}`;
    registry[internshipsRoute] = {
      route: internshipsRoute,
      pageType: "location",
      title: tier2InternshipsMeta.title,
      metaDescription: tier2InternshipsMeta.metaDescription,
      canonicalUrl: tier2InternshipsMeta.canonicalUrl,
      h1: tier2InternshipsMeta.h1,
      breadcrumbLabel: `${loc.cityName}, ${loc.stateAbbreviation} Internships`,
      sitemapInclusion: true,
      indexingStatus: "index, follow",
      lastmod: dateStr,
      structuredDataType: "Place",
      relatedRoutes: [jobsRoute, hiringRoute],
      publicationStatus: "published"
    };

    // Tier 3 Niche Job Intent Pages per city
    NICHE_JOB_INTENTS.forEach((niche) => {
      const cat = getJobCategoryBySlug(niche.slug);
      const catSlug = cat ? cat.slug : niche.slug;
      const nicheMeta = generateTier3NicheMetadata(loc, niche, "jobs");
      const nicheRoute = `/jobs/${loc.citySlug}/${catSlug}`;
      registry[nicheRoute] = {
        route: nicheRoute,
        pageType: "location",
        title: nicheMeta.title,
        metaDescription: nicheMeta.metaDescription,
        canonicalUrl: `${PRIMARY_DOMAIN}${nicheRoute}`,
        h1: nicheMeta.h1,
        breadcrumbLabel: nicheMeta.breadcrumbLabel,
        sitemapInclusion: true,
        indexingStatus: "index, follow",
        lastmod: dateStr,
        structuredDataType: "ItemList",
        relatedRoutes: [jobsRoute, "/locations"],
        publicationStatus: "published"
      };
    });
  });

  // 2b. Country Hub Pages
  getAllSupportedCountries().forEach((c) => {
    const dateStr = "2026-07-23";
    const countryVariants: Record<string, { title: string; metaDescription: string; h1: string }> = {
      "locations": {
        title: `${c.countryName} Local Business & City Directory | BizNearYou`,
        metaDescription: `Explore verified local businesses, commercial hubs, and city employer directories in ${c.countryName}.`,
        h1: `${c.countryName} Local Business & City Directory`
      },
      "jobs/locations": {
        title: `${c.countryName} Local Jobs & Hiring Directory | BizNearYou`,
        metaDescription: `Discover local job opportunities, hiring signals, and commercial growth across major cities in ${c.countryName}.`,
        h1: `${c.countryName} Local Job Opportunities & Employers`
      },
      "businesses-hiring/locations": {
        title: `${c.countryName} Businesses Hiring Directory | BizNearYou`,
        metaDescription: `Track commercial growth signals, expanding business sectors, and hiring momentum across ${c.countryName}.`,
        h1: `${c.countryName} Growing Businesses & Hiring Signals`
      },
      "internships/locations": {
        title: `${c.countryName} Student Internships Directory | BizNearYou`,
        metaDescription: `Find student internships, semester experience placements, and early-career contact points across ${c.countryName}.`,
        h1: `${c.countryName} Internship & Early-Career Opportunities`
      }
    };

    Object.entries(countryVariants).forEach(([prefix, meta]) => {
      const route = `/${prefix}/${c.countrySlug}`;
      registry[route] = {
        route,
        pageType: "location-directory",
        title: meta.title,
        metaDescription: meta.metaDescription,
        canonicalUrl: `${PRIMARY_DOMAIN}${route}`,
        h1: meta.h1,
        breadcrumbLabel: c.countryName,
        sitemapInclusion: true,
        indexingStatus: "index, follow",
        lastmod: dateStr,
        structuredDataType: "ItemList",
        relatedRoutes: ["/locations", "/jobs/locations"],
        publicationStatus: "published"
      };
    });
  });

  // 2c. Tier 1 State/Region Hub Pages
  getAllSupportedStates().forEach((st) => {
    const stateSlug = st.regionSlug || st.stateName.toLowerCase().replace(/\s+/g, "-");
    const dateStr = "2026-07-23";

    ["jobs", "businesses-hiring", "internships"].forEach((v) => {
      const route = `/${v}/${stateSlug}`;
      const stateMeta = generateTier1StateMetadata(st.stateName, stateSlug, v as "jobs" | "businesses-hiring" | "internships");

      registry[route] = {
        route,
        pageType: "location-directory",
        title: stateMeta.title,
        metaDescription: stateMeta.metaDescription,
        canonicalUrl: stateMeta.canonicalUrl,
        h1: stateMeta.h1,
        breadcrumbLabel: st.stateName,
        sitemapInclusion: true,
        indexingStatus: "index, follow",
        lastmod: dateStr,
        structuredDataType: "ItemList",
        relatedRoutes: ["/locations", "/jobs/locations"],
        publicationStatus: "published"
      };
    });
  });

  // 3. Industry Pages
  const industries = getAllIndustries();
  industries.forEach((ind) => {
    const route = `/industries/${ind.slug}`;
    registry[route] = {
      route,
      pageType: "industry",
      title: `${ind.name} - Local Employers & Career Paths | BizNearYou`,
      metaDescription: ind.shortDescription,
      canonicalUrl: `${PRIMARY_DOMAIN}${route}`,
      h1: `Discover ${ind.name} Near You`,
      breadcrumbLabel: ind.name,
      sitemapInclusion: true,
      indexingStatus: "index, follow",
      lastmod: "2026-07-23",
      structuredDataType: "WebPage",
      relatedRoutes: ["/industries", `/jobs/${ind.slug}/peoria-il`, `/jobs/${ind.slug}/chicago-il`],
      publicationStatus: "published"
    };

    // Qualified Industry-Location combinations (e.g. Peoria, Chicago, London)
    ["peoria-il", "chicago-il", "springfield-il", "toronto-on-ca", "london-eng-gb"].forEach((citySlug) => {
      const loc = getLocationBySlug(citySlug);
      if (loc && isLocationQualified(loc)) {
        const cat = getJobCategoryBySlug(ind.slug);
        const catSlug = cat ? cat.slug : ind.slug;
        const indLocRoute = `/jobs/${citySlug}/${catSlug}`;
        const cityState = `${loc.cityName}, ${loc.stateAbbreviation}`;
        const baseIndName = getCategoryBaseName(ind.name);
        registry[indLocRoute] = {
          route: indLocRoute,
          pageType: "industry-location",
          title: sanitizeDuplicateWords(`${baseIndName} Jobs & Employers in ${cityState} | BizNearYou`),
          metaDescription: sanitizeDuplicateWords(`Find independent ${baseIndName.toLowerCase()} businesses and employers in ${cityState}. Uncover unadvertised opportunities and direct contact info.`),
          canonicalUrl: `${PRIMARY_DOMAIN}${indLocRoute}`,
          h1: sanitizeDuplicateWords(`${baseIndName} Employers in ${cityState}`),
          breadcrumbLabel: `${baseIndName} - ${loc.cityName}`,
          sitemapInclusion: true,
          indexingStatus: "index, follow",
          lastmod: "2026-07-23",
          structuredDataType: "Place",
          relatedRoutes: [route, `/jobs/${citySlug}`, "/industries"],
          publicationStatus: "published"
        };
      }
    });
  });

  // 4. Guide Articles
  const guides = getAllGuides();
  guides.forEach((g) => {
    const route = `/guides/${g.slug}`;
    registry[route] = {
      route,
      pageType: "guide",
      title: g.title,
      metaDescription: g.metaDescription,
      canonicalUrl: `${PRIMARY_DOMAIN}${route}`,
      h1: g.h1,
      breadcrumbLabel: g.slug.replace(/-/g, " "),
      sitemapInclusion: true,
      indexingStatus: "index, follow",
      lastmod: g.lastUpdatedDate,
      structuredDataType: "Article",
      relatedRoutes: g.relatedGuideSlugs.map((s) => `/guides/${s}`),
      publicationStatus: "published",
      directAnswer: g.directAnswer,
      keyTakeaways: g.keyTakeaways,
      contentSections: g.contentSections
    };
  });

  // 5. Feature Pages
  const features = getAllFeatures();
  features.forEach((f) => {
    const route = `/features/${f.slug}`;
    registry[route] = {
      route,
      pageType: "feature",
      title: `${f.name} - Feature Overview | BizNearYou`,
      metaDescription: f.shortDescription,
      canonicalUrl: `${PRIMARY_DOMAIN}${route}`,
      h1: f.name,
      breadcrumbLabel: f.name,
      sitemapInclusion: true,
      indexingStatus: "index, follow",
      lastmod: "2026-07-23",
      structuredDataType: "SoftwareApplication",
      relatedRoutes: ["/features"],
      publicationStatus: "published"
    };
  });

  // 6. Report Pages
  const reports = getAllReports();
  reports.forEach((r) => {
    const route = `/reports/${r.slug}`;
    registry[route] = {
      route,
      pageType: "report",
      title: r.title,
      metaDescription: r.metaDescription,
      canonicalUrl: `${PRIMARY_DOMAIN}${route}`,
      h1: r.h1,
      breadcrumbLabel: r.slug.replace(/-/g, " "),
      sitemapInclusion: true,
      indexingStatus: "index, follow",
      lastmod: "2026-07-23",
      structuredDataType: "Article",
      relatedRoutes: ["/reports"],
      publicationStatus: "published"
    };
  });

  // 7. Methodology Articles
  const methodologies = getAllMethodologyArticles();
  methodologies.forEach((m) => {
    const route = `/methodology/${m.slug}`;
    registry[route] = {
      route,
      pageType: "methodology",
      title: m.title,
      metaDescription: m.metaDescription,
      canonicalUrl: `${PRIMARY_DOMAIN}${route}`,
      h1: m.h1,
      breadcrumbLabel: m.slug.replace(/-/g, " "),
      sitemapInclusion: true,
      indexingStatus: "index, follow",
      lastmod: m.lastUpdatedDate,
      structuredDataType: "WebPage",
      relatedRoutes: ["/how-it-works"],
      publicationStatus: "published"
    };
  });

  // 8. Compare Articles
  const compares = getAllCompareArticles();
  compares.forEach((c) => {
    const route = `/compare/${c.slug}`;
    registry[route] = {
      route,
      pageType: "compare",
      title: c.title,
      metaDescription: c.metaDescription,
      canonicalUrl: `${PRIMARY_DOMAIN}${route}`,
      h1: c.h1,
      breadcrumbLabel: c.slug.replace(/-/g, " "),
      sitemapInclusion: true,
      indexingStatus: "index, follow",
      lastmod: c.lastUpdatedDate,
      structuredDataType: "Article",
      relatedRoutes: ["/compare"],
      publicationStatus: "published"
    };
  });

  // 8. Job Categories & Employment Types
  const jobCategories = getAllJobCategoryDefinitions();
  jobCategories.forEach((jc) => {
    const route = `/jobs/${jc.slug}`;
    if (!registry[route]) {
      registry[route] = {
        route,
        pageType: "core",
        title: `${jc.name} & Local Employer Openings | BizNearYou`,
        metaDescription: jc.shortDescription,
        canonicalUrl: `${PRIMARY_DOMAIN}${route}`,
        h1: jc.heroHeadline,
        breadcrumbLabel: jc.name,
        sitemapInclusion: true,
        indexingStatus: "index, follow",
        lastmod: "2026-07-30",
        structuredDataType: "WebPage",
        relatedRoutes: ["/jobs", ...jc.relatedCategorySlugs.map(s => `/jobs/${s}`)],
        publicationStatus: "published"
      };
    }
  });

  // 9. Job Category Location Pages
  const qualifiedLocs = getAllQualifiedLocations();
  const topCategories = [
    "medical-healthcare",
    "dental-clinics",
    "plumbing-trades",
    "legal-accounting",
    "tech-agencies",
    "retail-hospitality",
    "retail-jobs",
    "restaurant-jobs",
    "healthcare-jobs",
    "construction-jobs"
  ];

  qualifiedLocs.forEach((loc) => {
    topCategories.forEach((catSlug) => {
      const jc = getJobCategoryBySlug(catSlug);
      if (jc) {
        const route = `/jobs/${loc.citySlug}/${jc.slug}`;
        if (!registry[route]) {
          const baseCatName = getCategoryBaseName(jc.name);
          registry[route] = {
            route,
            pageType: "location",
            title: sanitizeDuplicateWords(`${baseCatName} Jobs & Employers in ${loc.cityName}, ${loc.stateAbbreviation} | BizNearYou`),
            metaDescription: sanitizeDuplicateWords(`Discover ${baseCatName.toLowerCase()} jobs, local businesses, practices, offices and employers in ${loc.cityName}, ${loc.stateAbbreviation} on BizNearYou.`),
            canonicalUrl: `${PRIMARY_DOMAIN}${route}`,
            h1: sanitizeDuplicateWords(`${baseCatName} Jobs & Employers in ${loc.cityName}, ${loc.stateAbbreviation}`),
            breadcrumbLabel: baseCatName,
            sitemapInclusion: true,
            indexingStatus: "index, follow",
            lastmod: "2026-08-09",
            structuredDataType: "WebPage",
            relatedRoutes: [`/jobs/${loc.citySlug}`, `/jobs/${jc.slug}`],
            publicationStatus: "published"
          };
        }
      }
    });
  });

  // Final global normalization sweep on all titles, descriptions, and H1s
  Object.values(registry).forEach((entry) => {
    entry.title = sanitizeDuplicateWords(entry.title);
    entry.metaDescription = sanitizeDuplicateWords(entry.metaDescription);
    if (entry.h1) {
      entry.h1 = sanitizeDuplicateWords(entry.h1);
    }
  });

  return registry;
}

export const GLOBAL_SEO_REGISTRY = generateFullSeoRegistry();

export function getSeoEntryByRoute(route: string): SeoRegistryEntry | null {
  if (!route) return null;
  const cleanRoute = route.split("?")[0].toLowerCase();
  return GLOBAL_SEO_REGISTRY[cleanRoute] || null;
}

export function getAllSitemapRoutes(): SeoRegistryEntry[] {
  return Object.values(GLOBAL_SEO_REGISTRY).filter(
    (item) =>
      item.sitemapInclusion &&
      item.publicationStatus === "published" &&
      item.indexingStatus === "index, follow" &&
      getSeoRedirect(item.route) === null &&
      item.canonicalUrl === `${PRIMARY_DOMAIN}${item.route}` &&
      isSeoIndexEligible(item.route).isEligible
  );
}

export function getRelatedSeoEntries(currentRoute: string, limit = 4): SeoRegistryEntry[] {
  const current = getSeoEntryByRoute(currentRoute);
  if (!current) return [];

  const related: SeoRegistryEntry[] = [];
  current.relatedRoutes.forEach((relPath) => {
    const entry = getSeoEntryByRoute(relPath);
    if (entry && entry.publicationStatus === "published") {
      related.push(entry);
    }
  });

  // Fallback to fill limit if needed
  if (related.length < limit) {
    const all = Object.values(GLOBAL_SEO_REGISTRY);
    for (const item of all) {
      if (item.route !== currentRoute && !related.includes(item) && item.sitemapInclusion) {
        related.push(item);
        if (related.length >= limit) break;
      }
    }
  }

  return related.slice(0, limit);
}
